import { countryCodeToName } from "@oysterhr/common"
import {
  DaysSelector,
  HireHoursSelector,
  HireHoursSelectorProps,
  HireWorkDaysFormValues,
  type WorkDayValues,
} from "@oysterhr/feature-components"
import { Employment, WeekDaysEnum } from "@oysterhr/schema"
import { FormLabel, Text } from "@oysterhr/ui"
import { pickBy } from "lodash-es"
import { DateTime } from "luxon"
import { useCallback, useMemo } from "react"
import { UseFormReturn } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { sortDayOfWeek } from "./formHelpers"

const currentDate = DateTime.now()
const DEFAULT_FROM_TIME = currentDate.set({ hour: 9, minute: 0 }).toISO()
const DEFAULT_TO_TIME = currentDate.set({ hour: 17, minute: 0 }).toISO()

export type WorkScheduleFormValues = HireWorkDaysFormValues

type WorkScheduleProps = {
  employment: Employment
  form: UseFormReturn<WorkScheduleFormValues>
} & Pick<HireHoursSelectorProps, "onWorkScheduleChange">
export const WorkSchedule = ({ employment, form, onWorkScheduleChange }: WorkScheduleProps) => {
  const [t] = useTranslation("company_interface/CompanyNewHireFTEPage")

  const {
    watch,
    setValue,
    clearErrors,
    formState: { errors },
  } = form
  const watchedWorkDays = watch("workDays")
  const filteredWorkDays = useMemo(() => pickBy(watchedWorkDays), [watchedWorkDays])
  const selectedDays = useMemo(
    () => Object.keys(filteredWorkDays).sort(sortDayOfWeek),
    [filteredWorkDays],
  )
  const firstDayValues = useMemo(() => Object.values(filteredWorkDays)[0], [filteredWorkDays])

  const handleDaySelectClick = useCallback(
    (day: WeekDaysEnum, selected: boolean) => {
      let newWorkDaysValue = {
        ...watchedWorkDays,
        [day]: {
          from: DEFAULT_FROM_TIME,
          to: DEFAULT_TO_TIME,
        },
      }

      if (selected) {
        newWorkDaysValue = { ...watchedWorkDays }
        newWorkDaysValue[day] = undefined
      }
      setValue("workDays", newWorkDaysValue)
      onWorkScheduleChange?.(newWorkDaysValue)
    },
    [watchedWorkDays, setValue, onWorkScheduleChange],
  )

  const handleCopyToAllClick = useCallback(() => {
    const newWorkDays = selectedDays.reduce(
      (acc, key) => {
        return {
          ...acc,
          [key]: {
            from: firstDayValues?.from,
            to: firstDayValues?.to,
          },
        }
      },
      {} as Record<WeekDaysEnum, WorkDayValues>,
    )

    setValue("workDays", newWorkDays)
    clearErrors("workDays")
    onWorkScheduleChange?.(newWorkDays)
  }, [
    firstDayValues?.from,
    firstDayValues?.to,
    selectedDays,
    setValue,
    clearErrors,
    onWorkScheduleChange,
  ])

  const handleClearAllClick = useCallback(() => {
    const emptyWorkDays = {} as Record<WeekDaysEnum, WorkDayValues>

    setValue("workDays", emptyWorkDays)
    clearErrors("workDays")
    onWorkScheduleChange?.(emptyWorkDays)
  }, [clearErrors, onWorkScheduleChange, setValue])

  return (
    <div>
      <FormLabel
        label={t("label.workSchedule")}
        isOptional
        assistiveText={t("assistiveText.workSchedule", {
          countryName: countryCodeToName(employment.countryCode) || "",
        })}
      />
      <DaysSelector
        selectedDays={selectedDays}
        onDayClick={handleDaySelectClick}
        onClearAll={handleClearAllClick}
        className="mb-8 mt-4"
      />
      <HireHoursSelector
        selectedDays={selectedDays}
        onCopyToAllClick={handleCopyToAllClick}
        form={form}
        isClearable={false}
        onWorkScheduleChange={onWorkScheduleChange}
      />
      <Text size="small" className="mt-1.5 text-error-base">
        {errors?.workDays?.message}
      </Text>
    </div>
  )
}
